import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { reconcileOnce, isReconcileDone } from '../../lib/cloud/reconcile';

/**
 * Gate for authenticated routes. While the initial session resolves we show a
 * lightweight loader (avoids a flash of the login page on refresh). Logged-out
 * users are redirected to /login, remembering where they were headed.
 *
 * Once a session exists, we also run the one-time cloud<->localStorage reconcile
 * (chunk 3) and hold the data UI behind the same loader until it settles, so the
 * app reads reconciled localStorage rather than stale/empty values. The reconcile
 * runs once per app load (guarded), is fault-tolerant (falls back to local on any
 * error), and does NOT re-run on route changes.
 */
export function RequireAuth({ children }: { children: React.ReactElement }) {
  const { session, loading, isRecoverySession } = useAuth();
  const location = useLocation();
  const userId = session?.user?.id ?? null;

  const [reconciled, setReconciled] = useState<boolean>(() => isReconcileDone(userId));

  useEffect(() => {
    if (!userId) return;
    if (isReconcileDone(userId)) {
      setReconciled(true);
      return;
    }
    let active = true;
    setReconciled(false);
    // reconcileOnce never rejects, but guard both settle paths so the gate can
    // never get stuck on the loader.
    const finish = () => {
      if (active) setReconciled(true);
    };
    reconcileOnce(userId).then(finish, finish);
    return () => {
      active = false;
    };
  }, [userId]);

  const loader = (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
    </div>
  );

  if (loading) return loader;

  // A recovery-only session may not enter the app until the password is set.
  if (isRecoverySession) {
    return <Navigate to="/reset-password" replace />;
  }

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // Session exists but the first reconcile hasn't settled yet — brief loader.
  if (!reconciled) return loader;

  return children;
}
